// Slice 6 — Payslip + STP detail.
// Drill-in for a single pay run. Shows the per-employee payslip breakdown
// (wages, tax, super, net) plus a banner explaining where STP filing sits in
// the workflow. STP filing is automatic when the pay run is POSTED in Xero —
// we never push or amend STP from this app.

import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const kpiCardStyle: React.CSSProperties = {
  ...cardStyle,
  textAlign: 'center',
  flex: '1 1 160px',
  minWidth: 140,
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

function fmtMoney(n: number) {
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });
}

function formatDate(d: string) {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: BRISBANE_TZ,
  });
}

const STP_COPY: Record<
  'not-yet-filed' | 'filed-by-xero' | 'unknown',
  {
    label: string;
    background: string;
    border: string;
    body: string;
  }
> = {
  'not-yet-filed': {
    label: 'STP not yet filed',
    background: 'rgba(214, 147, 42, 0.12)',
    border: '#d6932a',
    body: "This pay run is still in DRAFT. Xero will lodge the STP report to the ATO automatically once you POST the pay run in Xero — there's nothing to do here.",
  },
  'filed-by-xero': {
    label: 'STP filed by Xero',
    background: 'rgba(72, 187, 120, 0.12)',
    border: '#48bb78',
    body: 'Xero filed the STP report to the ATO when this pay run was POSTED. Lodgement details live in Xero → Payroll → Pay Runs.',
  },
  unknown: {
    label: 'STP status unknown',
    background: 'rgba(127, 127, 127, 0.12)',
    border: '#7f7f7f',
    body: 'Could not infer STP status from the pay run.',
  },
};

export function PayrollPayRunDetail() {
  const { id } = useParams<{ id: string }>();
  const localId = id ? Number(id) : 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['pay-run-detail', localId],
    queryFn: () => api.payrollGetPayRun(localId),
    enabled: localId > 0,
  });

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 1200,
      }}
    >
      <div>
        <Link
          to="/payroll/pay-runs"
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
          }}
        >
          ← Back to pay runs
        </Link>
        <h1 style={{ margin: 0, marginTop: 4 }}>Payroll · Pay run detail</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          <strong>Slice 6</strong> — payslips Xero auto-generated for this pay run, plus where STP
          filing sits in the workflow.
        </div>
      </div>

      {isLoading ? (
        <div>Loading pay run…</div>
      ) : error ? (
        <div style={{ ...cardStyle, color: 'var(--color-danger, #e53e3e)' }}>
          Could not load pay run: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      ) : !data ? null : (
        <>
          <div style={cardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 'var(--spacing-3)',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <h2 style={{ margin: 0 }}>
                  {formatDate(data.periodStartDate)} → {formatDate(data.periodEndDate)}
                </h2>
                <div
                  style={{
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    marginTop: 4,
                  }}
                >
                  Payment date: {data.paymentDate ? formatDate(data.paymentDate) : '—'}
                </div>
              </div>
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: 999,
                  backgroundColor:
                    data.status === 'POSTED'
                      ? 'rgba(72, 187, 120, 0.15)'
                      : 'rgba(214, 147, 42, 0.15)',
                  color: data.status === 'POSTED' ? '#2f855a' : '#d6932a',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {data.status}
              </span>
            </div>
          </div>

          {/* Totals */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              flexWrap: 'wrap',
            }}
          >
            <div style={kpiCardStyle}>
              <div style={labelStyle}>Wages</div>
              <div
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                }}
              >
                {fmtMoney(data.totals.wages)}
              </div>
            </div>
            <div style={kpiCardStyle}>
              <div style={labelStyle}>Tax</div>
              <div
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                }}
              >
                {fmtMoney(data.totals.tax)}
              </div>
            </div>
            <div style={kpiCardStyle}>
              <div style={labelStyle}>Super</div>
              <div
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                }}
              >
                {fmtMoney(data.totals.super)}
              </div>
            </div>
            <div style={kpiCardStyle}>
              <div style={labelStyle}>Net pay</div>
              <div
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: 'var(--color-accent)',
                }}
              >
                {fmtMoney(data.totals.netPay)}
              </div>
            </div>
          </div>

          {/* STP banner */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              backgroundColor: STP_COPY[data.stpStatus].background,
              border: `1px solid ${STP_COPY[data.stpStatus].border}`,
              borderRadius: 'var(--radius-default)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <strong>{STP_COPY[data.stpStatus].label}.</strong> {STP_COPY[data.stpStatus].body}
          </div>

          {/* Payslips table */}
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0 }}>Payslips ({data.payslips.length})</h2>
            {data.payslips.length === 0 ? (
              <div
                style={{
                  padding: 'var(--spacing-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                  fontStyle: 'italic',
                }}
              >
                No payslips on this pay run.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Employee</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Wages</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Tax</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Super</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Reimb.</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Deduct.</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payslips.map((p) => (
                    <tr key={p.payslipId}>
                      <td style={tdStyle}>
                        <strong>{p.employeeName || '—'}</strong>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(p.wages)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(p.tax)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(p.super)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {p.reimbursements > 0 ? fmtMoney(p.reimbursements) : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {p.deductions > 0 ? fmtMoney(p.deductions) : '—'}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: 'right',
                          fontWeight: 700,
                          color: 'var(--color-accent)',
                        }}
                      >
                        {fmtMoney(p.netPay)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
